/**
 * Active segment state — kept in App component, persisted to localStorage so a
 * page refresh mid-segment resumes where the user left off.
 */
import type { PoolCard } from './cardPool';
import type { CardStatus, RoundExperience, UserRoundStatus } from '../types';

const STORAGE_PREFIX = 'gime5.segment.v1';

export interface SegmentTrace {
  round: number;
  cardId: string;
  cardTitle: string;
  actionUsed: string;
  returnPoint: string;
  userStatus: UserRoundStatus;
  experience: RoundExperience;
  ts: number;
}

export type SegmentSubscreen = 'three-cards' | 'timer' | 'trace';

export interface ActiveSegment {
  id: string;
  wish: string;
  cards: PoolCard[];
  round: number;            // 1..8
  currentCardId: string | null;  // the card the user just accepted the action on
  subscreen: SegmentSubscreen;
  traces: SegmentTrace[];
  startedAt: number;
}

const TOTAL_ROUNDS = 8;
export const SEGMENT_TOTAL_ROUNDS = TOTAL_ROUNDS;

function key(userId: string): string {
  return `${STORAGE_PREFIX}:${userId}`;
}

export function loadSegment(userId: string): ActiveSegment | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(key(userId));
    if (!raw) return null;
    return JSON.parse(raw) as ActiveSegment;
  } catch {
    return null;
  }
}

export function saveSegment(userId: string, seg: ActiveSegment | null): void {
  if (typeof window === 'undefined') return;
  if (!seg) {
    localStorage.removeItem(key(userId));
    return;
  }
  localStorage.setItem(key(userId), JSON.stringify(seg));
}

export function newSegment(wish: string, cards: PoolCard[]): ActiveSegment {
  return {
    id: `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    wish,
    cards,
    round: 1,
    currentCardId: null,
    subscreen: 'three-cards',
    traces: [],
    startedAt: Date.now(),
  };
}

/** Map UserRoundStatus → final CardStatus for the pool. */
export function cardStatusFromUserStatus(s: UserRoundStatus): CardStatus {
  if (s === 'done') return 'done';
  if (s === 'drop') return 'dropped';
  if (s === 'in_progress') return 'in_progress';
  return 'pending';
}
